import type { GoogleGeocodingResult } from "@/lib/geocoding"

/** How exactly a geocode locates the typed address; mirrors merchant-service-core `precisionOf`. */
export type AnchorPrecision = "exact" | "street" | "approximate"

/** How far the pin may sit from the typed address before the quote charges or refuses by the address. */
export const PIN_DRIFT_LIMIT_METERS: Record<AnchorPrecision, number | null> = {
  exact: 250,
  street: 800,
  approximate: null,
}

/** Grades a geocode: Google by location_type, CEP-only hits as CEP area, free street geocoders as street. */
export function geocodePrecision(result: GoogleGeocodingResult): AnchorPrecision {
  const placeId = result.place_id ?? ""
  const separator = placeId.indexOf(":")
  const tag = separator > 0 ? placeId.slice(0, separator) : ""
  const isGoogle = !tag || /[\s/]/.test(tag)
  if (isGoogle) {
    const type = (result.geometry?.location_type ?? "").toUpperCase()
    if (type === "ROOFTOP" || type === "RANGE_INTERPOLATED") return "exact"
    if (type === "GEOMETRIC_CENTER") return "street"
    return "approximate"
  }
  if (tag.endsWith("+merged")) return "street"
  if (result.types?.includes("postal_code")) return "approximate"
  if (/^(nominatim|photon|arcgis)/.test(tag)) return "street"
  return "approximate"
}

/** Great-circle distance between two points, in metres. */
export function distanceMeters(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const h =
    Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * 6371000 * Math.asin(Math.sqrt(h))
}

/** Where the typed address geocoded and how much the pin may drift from it; `null` limit = unbounded. */
export type PinAnchor = { lat: number; lng: number; limitMeters: number | null }

/** Whether the pin left the circle the anchor allows. */
export function pinOutOfReach(anchor: PinAnchor | null, pin: { lat: number; lng: number } | undefined): boolean {
  if (!anchor || anchor.limitMeters === null || !pin) return false
  return distanceMeters(anchor, pin) > anchor.limitMeters
}
